import {toRad} from '../../cdlx/utils/angles';

class StableLabeler {
	maxAngles = 12;

	run(labels = [], obstacles = [], width = 1, height = 1) {
		labels = this.clone(labels);
		const rotatedLabels = [];
		const stepAngle = 360 / this.maxAngles;
		for (let i = 0; i < labels.length; i++) {
			const label = labels[i];
			rotatedLabels[i] = new Array(this.maxAngles);
			for (let angleIndex = 0; angleIndex < this.maxAngles; angleIndex++) {
				const currentAngle = stepAngle * angleIndex;
				const rotatedLabel =
					angleIndex === 0 ? {...label} : this.rotateLabel(label, currentAngle, width, height);
				rotatedLabels[i][angleIndex] = rotatedLabel;
			}
		}

		labels = this.solveOutOfBounds(labels, rotatedLabels, width, height);

		let conflictingGroups = [];
		let iterations = 100;
		do {
			conflictingGroups = this.getConflictingGroups(labels);
			// console.log('conflicts', conflictingGroups);
			if (conflictingGroups.length) {
				for (const conflictingLabels of conflictingGroups) {
					const solvedLabels = this.solveConflicts(
						this.clone(labels),
						conflictingLabels,
						rotatedLabels,
						obstacles
					);
					// console.log('solved', solvedLabels);
					for (const solvedLabel of solvedLabels) {
						labels[+solvedLabel.index] = solvedLabel;
					}
				}
			}
			iterations--;
		} while (conflictingGroups.length > 0 && iterations > 0);
		return labels;
	}

	solveOutOfBounds(labels, rotatedLabels, width, height) {
		const base = this.maxAngles;
		const half = base / 2;
		const stepAngle = 360 / this.maxAngles;
		for (let i = 0; i < labels.length; i++) {
			const label = labels[i];
			if (this.isOutOfBounds(label.x, label.y, label.width, label.height, width, height)) {
				// console.log('out', label.name);
				let solution = null;
				let solutionDistance = null;
				for (let angleIndex = 0; angleIndex < this.maxAngles; angleIndex++) {
					// const currentAngle = stepAngle * angleIndex;
					const rotatedLabel = rotatedLabels[i][angleIndex];
					if (
						rotatedLabel !== null &&
						!this.isOutOfBounds(
							rotatedLabel.x,
							rotatedLabel.y,
							rotatedLabel.width,
							rotatedLabel.height,
							width,
							height
						)
					) {
						const distance = angleIndex > half ? base - angleIndex : angleIndex;
						if (solution === null || solutionDistance > distance) {
							solution = rotatedLabel;
							solutionDistance = distance;
							// console.log(
							// 	'solved',
							// 	label.name,
							// 	angleIndex,
							// 	label.x,
							// 	rotatedLabel.x,
							// 	label.y,
							// 	rotatedLabel.y
							// );
						}
					}
				}
				if (solution !== null) {
					labels[i] = solution;
					rotatedLabels[i] = new Array(this.maxAngles);
					for (let angleIndex = 0; angleIndex < this.maxAngles; angleIndex++) {
						const currentAngle = stepAngle * angleIndex;
						const rotatedLabel =
							angleIndex === 0
								? {...solution}
								: this.rotateLabel(solution, currentAngle, width, height);
						rotatedLabels[i][angleIndex] = rotatedLabel;
					}
				}
			}
		}
		return labels;
	}

	solveConflicts(labels, conflictingLabels, rotatedLabels, obstacles) {
		let solutionDistance = null;
		let solution = [];
		let solutionObstaclesRatio = null;
		const digits = conflictingLabels.length;
		const base = this.maxAngles;
		const half = base / 2;
		const total = Math.pow(base, digits);
		for (let i = 0; i < total; i++) {
			const str = i.toString(base).padStart(digits, '0');
			const combination = str.split('').map((value) => parseInt(value, base));
			const rotated = [];
			for (let labelIndex = 0; labelIndex < combination.length; labelIndex++) {
				const realLabelIndex = +conflictingLabels[labelIndex].index;
				const angleIndex = combination[labelIndex];
				const rotatedLabel = rotatedLabels[realLabelIndex][angleIndex];
				rotated.push(rotatedLabel);
			}
			let invalid = false;
			for (const rotatedLabel of rotated) {
				if (rotatedLabel === null) {
					invalid = true;
					break;
				}
			}
			if (invalid) {
				// console.log('skip invalid solution');
				continue;
			}
			for (const rotatedLabel of rotated) {
				labels[+rotatedLabel.index] = rotatedLabel;
			}

			if (!this.groupHasConflicts(rotated, labels)) {
				let distance = 0;
				let obstaclesRatio = 0;
				for (const angleIndex of combination) {
					distance += angleIndex > half ? base - angleIndex : angleIndex;
				}
				// penalty for label-obstacle overlap
				for (const l1 of rotated) {
					for (let o = 0; o < obstacles.length; o++) {
						const l2 = obstacles[o];
						const xOverlap = Math.max(0, Math.min(l1.x2, l2.x2) - Math.max(l1.x1, l2.x1));
						const yOverlap = Math.max(0, Math.min(l1.y2, l2.y2) - Math.max(l1.y1, l2.y1));
						const overlapArea = xOverlap * yOverlap;
						obstaclesRatio += overlapArea;
					}
				}
				if (
					solutionDistance === null ||
					solutionDistance > distance ||
					(solutionDistance === distance && solutionObstaclesRatio > obstaclesRatio)
				) {
					// console.log('better solution', distance, combination, solutionObstaclesRatio);
					solution = rotated;
					solutionDistance = distance;
					solutionObstaclesRatio = obstaclesRatio;
				}
			}
		}
		return solution;
	}

	getConflictingGroups(labels) {
		const groups = [];
		for (let i = 0; i < labels.length - 1; i++) {
			for (let j = i + 1; j < labels.length; j++) {
				const l1 = labels[i];
				const l2 = labels[j];
				if (this.labelsHaveConflicts(l1, l2)) {
					let found = false;
					for (const group of groups) {
						const hasL1 = group.includes(l1);
						const hasL2 = group.includes(l2);
						if (hasL1 || hasL2) {
							found = true;
							if (!hasL1) {
								group.push(l1);
							}
							if (!hasL2) {
								group.push(l2);
							}
							break;
						}
					}
					if (!found) {
						const group = [l1, l2];
						groups.push(group);
					}
				}
			}
		}
		return groups;
	}

	groupHasConflicts(group, labels) {
		for (let i = 0; i < group.length; i++) {
			const l1 = group[i];
			for (let j = 0; j < labels.length; j++) {
				const l2 = labels[j];
				if (l1.index !== l2.index && this.labelsHaveConflicts(l1, l2)) {
					return true;
				}
			}
		}
		return false;
	}

	labelsHaveConflicts(l1, l2) {
		if (
			this.hasCollision(l1, l2) ||
			this.intersect(l1.x, l1.y, l1.cx, l1.cy, l2.x, l2.y, l2.cx, l2.cy) ||
			this.lineRectIntersection(l2.x, l2.y, l2.cx, l2.cy, l1.x1, l1.y1, l1.x2, l1.y2) ||
			this.lineRectIntersection(l1.x, l1.y, l1.cx, l1.cy, l2.x1, l2.y1, l2.x2, l2.y2) ||
			this.hasCenterCollision(l1, l2) ||
			this.hasCenterCollision(l2, l1)
		) {
			return true;
		}
		return false;
	}

	rotateLabel(label, angle, width, height) {
		label = {...label};
		const newPoint = this.rotatePoint(label.x, label.y, label.cx, label.cy, toRad(angle));

		// hard wall boundaries

		// const tolerance = 35;
		// if (
		// 	newPoint.x + label.width / 2 > width + tolerance ||
		// 	newPoint.x - label.width / 2 < 0 - tolerance ||
		// 	newPoint.y + label.height / 2 > height + tolerance ||
		// 	newPoint.y - label.height / 2 < 0 - tolerance
		// ) {
		if (this.isOutOfBounds(newPoint.x, newPoint.y, label.width, label.height, width, height)) {
			return null;
		}
		label.x = newPoint.x;
		label.y = newPoint.y;
		label.x1 = label.x - label.width / 2;
		label.x2 = label.x1 + label.width;
		label.y1 = label.y - label.height / 2;
		label.y2 = label.y1 + label.height;
		return label;
	}

	isOutOfBounds(x, y, width, height, containerWidth, containerHeight, tolerance = 0) {
		return (
			x + width / 2 > containerWidth + tolerance ||
			x - width / 2 < 0 - tolerance ||
			y + height / 2 > containerHeight + tolerance ||
			y - height / 2 < 0 - tolerance
		);
	}

	clone(items) {
		return items.map((item) => ({...item}));
	}

	rotatePoint(pointX, pointY, originX, originY, angle) {
		// angle = angle * Math.PI / 180.0;
		return {
			x: Math.cos(angle) * (pointX - originX) - Math.sin(angle) * (pointY - originY) + originX,
			y: Math.sin(angle) * (pointX - originX) + Math.cos(angle) * (pointY - originY) + originY,
		};
	}

	hasCollision(a, b) {
		return !(a.y2 < b.y1 || a.y1 > b.y2 || a.x2 < b.x1 || a.x1 > b.x2);
	}

	hasCenterCollision(a, b) {
		return !(a.y2 < b.cy1 || a.y1 > b.cy2 || a.x2 < b.cx1 || a.x1 > b.cx2);
	}

	// returns true if the line from (a,b)->(c,d) intersects with (p,q)->(r,s)
	intersect(a, b, c, d, p, q, r, s) {
		const det = (c - a) * (s - q) - (r - p) * (d - b);
		if (det === 0) {
			return false;
		} else {
			const lambda = ((s - q) * (r - a) + (p - r) * (s - b)) / det;
			const gamma = ((b - d) * (r - a) + (c - a) * (s - b)) / det;
			return lambda > 0 && lambda < 1 && gamma > 0 && gamma < 1;
		}
	}

	lineRectIntersection(x1, y1, x2, y2, bx1, by1, bx2, by2) {
		if (
			(x1 >= bx1 && x1 <= bx2 && y1 >= by1 && y1 <= by2) ||
			(x2 >= bx1 && x2 <= bx2 && y2 >= by1 && y2 <= by2) ||
			this.intersect(x1, y1, x2, y2, bx1, by1, bx1, by2) ||
			this.intersect(x1, y1, x2, y2, bx1, by1, bx2, by1) ||
			this.intersect(x1, y1, x2, y2, bx1, by2, bx2, by2) ||
			this.intersect(x1, y1, x2, y2, bx2, by1, bx2, by2)
		) {
			return true;
		}
		return false;
	}
}

export {StableLabeler};
